import type { Breadcrumb } from '~/apps/search/schemas'
import type { SearchFilters } from '~/types/search'
import type { FacetRemovalRule } from '~/utility/facets'
import type { SimpleBreadcrumbWithCount } from '~/components/search/types'
import { stripFacetsFromUrl } from './stripFacetsFromUrl'

export interface ComputeBreadcrumbsOptions {
  breadcrumbs: Breadcrumb[]
  counts?: Record<string, number>
  searchFilters: SearchFilters
  facetRules: FacetRemovalRule[] | null
  hierarchyCounts: Record<string, number>
  facetedHierarchyCounts: Record<string, number>
  isGlampingCategory: boolean
  isDatedSearch: boolean
  getBaseUrl: (useSearch: boolean) => string
}

/**
 * Pure function to compute breadcrumb display data from raw breadcrumb input.
 *
 * This function is framework-agnostic and contains all the business logic for:
 * - Determining which breadcrumbs to show
 * - Computing breadcrumb URLs (SEO vs non-SEO)
 * - Handling facet tier insertion
 * - Managing facet query parameter removal
 *
 * @param options - All inputs needed for breadcrumb computation
 * @returns Array of breadcrumbs with computed display properties
 */
export function computeBreadcrumbs(
  options: ComputeBreadcrumbsOptions,
): SimpleBreadcrumbWithCount[] {
  const {
    breadcrumbs,
    counts,
    searchFilters,
    facetRules,
    hierarchyCounts,
    facetedHierarchyCounts,
    isGlampingCategory,
    isDatedSearch,
    getBaseUrl,
  } = options

  const newBreadcrumbs: SimpleBreadcrumbWithCount[] = []
  let removedFacetBreadcrumb: Breadcrumb | null = null
  const hasFacets = (searchFilters.facets ?? []).length > 0

  breadcrumbs.forEach((breadcrumb, index) => {
    if (breadcrumb.name === 'Pitchup.com') {
      return
    }

    // Non-faceted count: used for "< 5" threshold check and other logic
    const hierarchyCount = breadcrumb.hierarchyPath
      ? hierarchyCounts[breadcrumb.hierarchyPath]
      : undefined

    // Faceted count: used for linkable check to avoid linking to zero-result pages
    const facetedHierarchyCount = breadcrumb.hierarchyPath
      ? facetedHierarchyCounts[breadcrumb.hierarchyPath]
      : undefined

    const count = breadcrumb.hierarchyPath
      ? counts?.[breadcrumb.hierarchyPath]
      : undefined

    const _countLevel = hasFacets ? 5 : 1
    const shouldRemove = facetRules?.some((rule) => {
      if (rule.category_all) {
        return true
      }
      if (rule.category_campsites && !searchFilters.categoryIds?.length) {
        return true
      }

      // FIXED: For glamping pages, only apply glamping-specific rules
      if (isGlampingCategory) {
        if (rule.category_glamping) {
          return true
        }
        // Skip individual category rules for glamping pages
        return false
      }

      // For non-glamping pages, check individual category matches
      const currentCategoryIds = searchFilters.categoryIds || []
      const categoryMatch = currentCategoryIds.some((id) =>
        rule.category_ids.includes(Number(id)),
      )
      if (categoryMatch) {
        return true
      }
      return false
    })

    // Check early return condition (facet breadcrumb at index 2)
    const shouldEarlyReturn = hasFacets && shouldRemove && index === 2

    if (shouldEarlyReturn) {
      // Track the removed facet breadcrumb so we can add it back later with correct URL
      removedFacetBreadcrumb = breadcrumb
      return
    }
    // Use faceted count for linkable - don't link if the faceted search has no results
    // If breadcrumb has hierarchyPath but facetedHierarchyCount is undefined,
    // check if facetedHierarchyCounts object is empty (meaning zero total results)
    let linkable: boolean
    if (
      breadcrumb.hierarchyPath &&
      typeof facetedHierarchyCount === 'undefined'
    ) {
      // Has hierarchyPath but count is undefined - check if all counts are zero/empty
      const hasFacets = (searchFilters.facets ?? []).length > 0
      if (hasFacets) {
        // If facet present and counts object is empty, means zero results - not linkable
        linkable = Object.keys(facetedHierarchyCounts).length > 0
      } else {
        // No facet, undefined count means linkable (fallback to old behavior)
        linkable = true
      }
    } else {
      // Normal case: link if target page has any results (> 0)
      // The < 5 threshold is ONLY for URL format (nonSeo), not for linkability
      // Use facetedCount if available, else count, else default to linkable (1)
      linkable = (facetedHierarchyCount ?? count ?? 1) > 0
    }

    // FIXED: Category root (index 1) should always use SEO URL, not /search/
    // It should just remove the facet parameter via removeFacetQuery
    const isCategoryRoot = index === 1
    const shouldUseSeoUrl = isCategoryRoot || (!shouldRemove && !isDatedSearch)

    const url =
      breadcrumb.hierarchyPath === '/'
        ? shouldUseSeoUrl
          ? breadcrumb.url
          : `${getBaseUrl(true)}/`
        : `${getBaseUrl(Boolean(shouldRemove) || isDatedSearch)}/${breadcrumb.hierarchyPath}`

    // FIXED: Always remove facet query from category root (index 1) when facets present
    // The facet now has its own tier in the breadcrumb, so the category root should never have ?facet=
    let removeFacetQuery = false
    if (index === 1 && hasFacets) {
      removeFacetQuery = true
    }

    // Category root should not be marked as nonSeo even when facets present
    // Use non-faceted count for "< 5" threshold to determine URL format (matches CSD logic)
    // Only apply lowCountCheck to hierarchy breadcrumbs (not category root or facet tier)
    const isHierarchyBreadcrumb =
      breadcrumb.hierarchyPath && breadcrumb.hierarchyPath !== '/'
    const lowCountCheck =
      isHierarchyBreadcrumb && (hierarchyCount ?? 0) < 5 && hasFacets
    const isNonSeo = isCategoryRoot
      ? isDatedSearch
      : Boolean(shouldRemove) || lowCountCheck || isDatedSearch

    newBreadcrumbs.push({
      label: breadcrumb.name,
      url: url,
      count,
      hierarchyLevel:
        breadcrumb.hierarchyLevel !== null
          ? breadcrumb.hierarchyLevel
          : undefined,
      hierarchyPath:
        breadcrumb.hierarchyPath !== null
          ? breadcrumb.hierarchyPath
          : undefined,
      linkable,
      nonSeo: isNonSeo,
      to: { path: url },
      removeFacetQuery,
      debugData: {
        hierarchyCount,
        linkable,
        nonSeo: isNonSeo,
        hierarchyPath: breadcrumb.hierarchyPath ?? undefined,
        level: breadcrumb.hierarchyLevel ?? undefined,
      },
    })
  })

  // Add back the facet tier if it was removed
  if (removedFacetBreadcrumb && searchFilters.facets?.length === 1) {
    const facetSlug = searchFilters.facets[0]

    // FIXED: Determine if we should use SEO URL or query param
    // Use ?facet= if the facet is removed for the current category (same logic as shouldRemove)
    const useQueryParam = facetRules?.some((rule) => {
      if (rule.category_all) {
        return true
      }
      if (rule.category_campsites && !searchFilters.categoryIds?.length) {
        return true
      }

      // For glamping pages, only apply glamping-specific rules
      if (isGlampingCategory) {
        if (rule.category_glamping) {
          return true
        }
        // Skip individual category rules for glamping pages
        return false
      }

      // For non-glamping pages, check individual category matches
      const currentCategoryIds = searchFilters.categoryIds || []
      const categoryMatch = currentCategoryIds.some((id) =>
        rule.category_ids.includes(Number(id)),
      )
      if (categoryMatch) {
        return true
      }
      return false
    })

    // Build the facet breadcrumb URL
    // FIXED: Facet tier should only link to base search URL, not include hierarchy
    const facetUrl = useQueryParam
      ? `${getBaseUrl(true)}/` // Base search URL, ?facet= added via query in SearchBreadcrumbsGenerator
      : `${getBaseUrl(false)}/-/${facetSlug}/`

    // FIXED: Insert facet tier after category root (position 1), not before last hierarchy item
    // Example: Home › Campsites › [INSERT FACET HERE] › England › South West › Devon
    const facetBreadcrumb = {
      label: removedFacetBreadcrumb.name as string,
      url: facetUrl,
      count: undefined,
      hierarchyLevel: undefined,
      hierarchyPath: '/',
      linkable: true,
      nonSeo: useQueryParam,
      to: { path: facetUrl },
      removeFacetQuery: false,
      debugData: {
        hierarchyCount: undefined,
        linkable: true,
        nonSeo: useQueryParam,
        hierarchyPath: '/',
        level: undefined,
      },
    }

    // Insert after category root (index 0 is category root, insert at position 1)
    newBreadcrumbs.splice(1, 0, facetBreadcrumb)
  }

  return newBreadcrumbs
}

/**
 * Compute secondary breadcrumbs for faceted search pages.
 *
 * This function creates a simplified breadcrumb trail showing the unfaceted
 * hierarchy path (category + geographic location only, no facets).
 *
 * Purpose: On faceted search pages like `/campsites/England/Hampshire/-/electric/`,
 * provide a secondary navigation path to the unfaceted parent `/campsites/England/Hampshire/`.
 *
 * Key differences from primary breadcrumbs:
 * - No facet tier insertion
 * - All URLs have facets stripped (no `-/facet/` segments)
 * - Simpler linkability logic (based on hierarchy counts only)
 * - Always uses SEO URLs (no query param format)
 *
 * @param options - Same inputs as computeBreadcrumbs
 * @returns Array of breadcrumbs with unfaceted URLs
 */
export function computeSecondaryBreadcrumbs(
  options: ComputeBreadcrumbsOptions,
): SimpleBreadcrumbWithCount[] {
  const { breadcrumbs, counts, hierarchyCounts, getBaseUrl } = options

  const newBreadcrumbs: SimpleBreadcrumbWithCount[] = []

  breadcrumbs.forEach((breadcrumb, index) => {
    // Skip Pitchup.com root
    if (breadcrumb.name === 'Pitchup.com') {
      return
    }

    // Skip facet breadcrumbs (they appear at index 2 in the raw breadcrumb data
    // when facets are present)
    // We identify facet breadcrumbs by checking if they lack a hierarchyPath
    // or have hierarchyPath === '/' which indicates they're not part of the geographic hierarchy
    const isFacetBreadcrumb =
      !breadcrumb.hierarchyPath || breadcrumb.hierarchyPath === '/'
    if (isFacetBreadcrumb && index > 1) {
      return
    }

    // Use hierarchy count (without facets) for linkability
    const hierarchyCount = breadcrumb.hierarchyPath
      ? hierarchyCounts[breadcrumb.hierarchyPath]
      : undefined

    const count = breadcrumb.hierarchyPath
      ? counts?.[breadcrumb.hierarchyPath]
      : undefined

    // Determine linkability based on hierarchy count (without facets)
    const linkable =
      typeof hierarchyCount === 'undefined' ||
      hierarchyCount >= 1 ||
      typeof count === 'undefined'

    // Build URL without facets - strip any facet segments
    const baseUrl =
      breadcrumb.hierarchyPath === '/'
        ? breadcrumb.url
        : `${getBaseUrl(false)}/${breadcrumb.hierarchyPath}`

    const url = stripFacetsFromUrl(baseUrl)

    newBreadcrumbs.push({
      label: breadcrumb.name,
      url,
      count,
      hierarchyLevel:
        breadcrumb.hierarchyLevel !== null
          ? breadcrumb.hierarchyLevel
          : undefined,
      hierarchyPath:
        breadcrumb.hierarchyPath !== null
          ? breadcrumb.hierarchyPath
          : undefined,
      linkable,
      nonSeo: false, // Always use SEO URLs for secondary breadcrumbs
      to: { path: url },
      removeFacetQuery: true, // Always remove facet query params
      debugData: {
        hierarchyCount,
        linkable,
        nonSeo: false,
        hierarchyPath: breadcrumb.hierarchyPath ?? undefined,
        level: breadcrumb.hierarchyLevel ?? undefined,
      },
    })
  })

  return newBreadcrumbs
}
